import { useState, useRef, useEffect } from 'react'
import { useTranslation } from '../../contexts/LanguageContext'
import type { CVDocument } from '../../services/firebase/firestore'

interface CVListCompactProps {
  cv: CVDocument
  isActive: boolean
  onSelect: () => void
  onDelete: () => void
  onRename?: (newTitle: string) => void
}

export function CVListCompact({ cv, isActive, onSelect, onDelete, onRename }: CVListCompactProps) {
  const { t, language } = useTranslation()
  const [showActions, setShowActions] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(cv.title || t.cvList.noTitle)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  function formatRelativeDate(date: Date): string {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return t.cvList.now
    if (minutes < 60) return t.cvList.minutesAgo.replace('{n}', String(minutes))
    if (hours < 24) return t.cvList.hoursAgo.replace('{n}', String(hours))
    if (days < 7) return t.cvList.daysAgo.replace('{n}', String(days))
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' })
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm(t.cvList.deleteConfirm)) {
      onDelete()
    }
  }

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditTitle(cv.title || t.cvList.noTitle)
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== cv.title && onRename) {
      onRename(trimmed)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditTitle(cv.title || t.cvList.noTitle)
    }
  }

  return (
    <div
      onClick={isEditing ? undefined : onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      className={`group relative p-3 rounded-xl transition-all duration-200 ${
        isEditing ? '' : 'cursor-pointer'
      } ${
        isActive
          ? 'bg-primary/10 border-2 border-primary shadow-sm'
          : 'bg-white border-2 border-transparent hover:bg-gray-50 hover:border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isActive ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
          }`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 text-sm font-medium border border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          ) : (
            <h3
              className={`font-medium truncate ${
                isActive ? 'text-primary' : 'text-gray-900'
              }`}
            >
              {cv.title || t.cvList.noTitle}
            </h3>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {formatRelativeDate(cv.updatedAt)}
          </p>
        </div>

        {/* Action buttons */}
        {!isEditing && (
          <div
            className={`absolute top-2 right-2 flex gap-1 transition-all duration-200 ${
              showActions ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Edit button */}
            {onRename && (
              <button
                onClick={handleStartEdit}
                className="p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                title={t.cvList.rename}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            {/* Delete button */}
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
              title={t.cvList.delete}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
      )}
    </div>
  )
}
